import React, { useEffect, useState, useRef } from "react";
import clsx from "clsx";
import Select from "react-select";
import MarketingMaterials from "./MarketingMaterials";
import FlavorPrices from "./FlavorPrices";
import LocationChecks from "./LocationChecks";
import { GetFlavorsType, getVideo } from "../../../models/_publicCall";
import { GetMarketingMaterialForFilterType } from "../../../models/_marketingMaterial";
import { INSTALLATION_JOB_STORAGE_KEY } from "../InstallationJob";
import SimpleMobileStepper from "../../Stepper/Stepper";
import { Tooltip } from "react-tooltip";

interface HeaderType {
  id: string;
  name: string;
}

const LocationInformation = ({
  headerDB,
  setCurrentStep,
  InstalltionFormSteps,
  flavors,
  materialDB,
  jobStatus,
  installationJobDB,
  videoOptions,
  fetchVideos,
  installationJobStepsRequired,
}: {
  headerDB: HeaderType[];
  setCurrentStep: (step: number) => void;
  InstalltionFormSteps: any;
  flavors: GetFlavorsType[];
  materialDB: GetMarketingMaterialForFilterType[];
  jobStatus: string;
  installationJobDB: any;
  videoOptions: { value: string; label: string }[];
  fetchVideos: () => void;
  installationJobStepsRequired: { id: number; label: string }[];
}) => {
  const [selectedHeader, setSelectedHeader] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [locationNumber, setLocationNumber] = useState<string>("");
  const locationChecksRef = useRef<any>(null); // 👈 Reference to LocationChecks
  const [videos, setVideos] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    setVideos(videoOptions);
  }, [videoOptions]);

  useEffect(() => {
    fetchVideos();
  }, []);

  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!selectedHeader) newErrors.header = "Header type is required";
    if (selectedHeader === "TV" && !selectedVideo)
      newErrors.video = "Video for header is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (locationChecksRef.current && !locationChecksRef.current.validateSim()) {
      return;
    }

    if (!validateForm()) return;

    const locationData = {
      headerType: selectedHeader,
      videoUuid: selectedVideo, // Add selected video UUID
    };

    const storedData = JSON.parse(
      localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}",
    );

    const updatedData = {
      ...storedData,
      locationInformation: {
        ...storedData.locationInformation,
        ...locationData,
      },
    };

    localStorage.setItem(
      INSTALLATION_JOB_STORAGE_KEY,
      JSON.stringify(updatedData),
    );

    setCurrentStep(InstalltionFormSteps.LOCATION_CONTACTS);
  };

  useEffect(() => {
    const savedData = JSON.parse(
      localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}",
    );
    const locationData = savedData.locationInformation;
    setLocationNumber(savedData.locationSelection.locationNumber || "");
    const hasLocationInfoFields =
      locationData && (locationData.headerType || locationData.videoUuid);

    if (hasLocationInfoFields) {
      if (locationData.headerType) setSelectedHeader(locationData.headerType);
      if (locationData.videoUuid) setSelectedVideo(locationData.videoUuid);
      return;
    }

    if (jobStatus !== "Completed" && installationJobDB) {
      const dbHeader = installationJobDB.headerType || null;
      const dbVideo = installationJobDB.videoUuid || null;

      if (dbHeader) setSelectedHeader(dbHeader);
      if (dbVideo) setSelectedVideo(dbVideo);

      const updatedData = {
        ...savedData,
        locationInformation: {
          ...locationData,
          headerType: dbHeader,
          videoUuid: dbVideo,
          selectedMarketingMaterials:
            locationData?.selectedMarketingMaterials ||
            installationJobDB.marketingMaterials ||
            [],
          flavorPrices:
            locationData?.flavorPrices || installationJobDB.flavorPrice || [],
        },
      };

      localStorage.setItem(
        INSTALLATION_JOB_STORAGE_KEY,
        JSON.stringify(updatedData),
      );
    }
  }, [jobStatus, installationJobDB]);

  return (
    <div>
      <header className="header">
        <h2>Location Information</h2>
      </header>

      {/* <div
        className="left-icon m-4 mt-4"
        onClick={() => setCurrentStep(InstalltionFormSteps.MACHINE_DATA)}
        style={{ cursor: "pointer" }}
      >
        <i className="fa fa-arrow-left" aria-hidden="true"></i>
      </div> */}

      <div className="job-wrapper">
        <div className="row mb-3 mt-8">
          {/* Location Number */}
          <div className="col-12 col-md-4">
            <label className="wo-label fs-5 fw-bold">Location Number:</label>
          </div>
          <div className="col-12 col-md-8">
            <label className="wo-label fs-5 fw-bold">{locationNumber}</label>
          </div>
        </div>

        {/* Header Type */}
        <div className="row align-items-center mb-3 ">
          <div className="col-12 col-md-4">
            <label className="wo-label fs-5 fw-bold required location-info-header-type-label">
              Header Type:
            </label>
            <Tooltip
              anchorSelect=".location-info-header-type-label"
              data-placement="top"
            >
              Select the type of header display for the machine
            </Tooltip>
          </div>
          <div className="col-12 col-md-8">
            <Select
              isSearchable
              className={clsx("react-select")}
              classNamePrefix="my-react-select"
              placeholder="Select Header Type"
              options={headerDB.map((e) => ({
                value: e.id,
                label: e.name,
              }))}
              value={
                selectedHeader
                  ? headerDB
                      .map((e) => ({ value: e.id, label: e.name }))
                      .find((option) => option.value === selectedHeader)
                  : null
              }
              onChange={(option) => {
                setSelectedHeader(option ? option.value : null);
                clearError("header");
              }}
            />
            {errors.header && (
              <div className="text-danger">{errors.header}</div>
            )}
          </div>
        </div>

        {/* Video for header */}
        {selectedHeader === "TV" && (
          <div className="row align-items-center mb-3 mt-8">
            <div className="col-12 col-md-4">
              <label className="wo-label fs-5 fw-bold required location-info-video-label">
                Video for header:
              </label>
              <Tooltip
                anchorSelect=".location-info-video-label"
                data-placement="top"
              >
                Select a video to display on the TV header (required when Header
                Type is TV)
              </Tooltip>
            </div>
            <div className="col-12 col-md-8 d-flex align-items-center gap-2">
              <div style={{ flex: 1 }}>
                <Select
                  isSearchable
                  className={clsx("react-select")}
                  classNamePrefix="my-react-select"
                  placeholder="Select Video"
                  options={videos}
                  value={
                    selectedVideo
                      ? videos.find((option) => option.value === selectedVideo)
                      : null
                  }
                  onChange={(option) =>
                    setSelectedVideo(option ? option.value : null)
                  }
                />
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                title="Refresh Videos"
                onClick={fetchVideos}
                style={{ minWidth: 40 }}
              >
                <i className="fa fa-refresh" aria-hidden="true"></i>
              </button>
              {errors.video && (
                <div className="text-danger ms-2">{errors.video}</div>
              )}
            </div>
          </div>
        )}

        <MarketingMaterials
          materialDB={materialDB}
          installationJobDB={installationJobDB}
          jobStatus={jobStatus}
        />
        <FlavorPrices
          flavors={flavors}
          jobId="1"
          installationJobDB={installationJobDB}
          jobStatus={jobStatus}
        />
        <LocationChecks
          ref={locationChecksRef}
          installationJobDB={installationJobDB}
          jobStatus={jobStatus}
        />

        {/* <div className="text-end mt-3">
          <button type="button" className="btn btn-primary" onClick={handleNext}>
            <i className="fa fa-long-arrow-right" aria-hidden="true"></i> Next
          </button>
        </div> */}
      </div>
      <SimpleMobileStepper
        totalSteps={installationJobStepsRequired.length}
        handleNext={handleNext}
        handleBack={() => setCurrentStep(InstalltionFormSteps.MACHINE_DATA)}
        activeStep={installationJobStepsRequired.findIndex(
          (s) => s.id === InstalltionFormSteps.LOCATION_INFORMATION,
        )}
      />
    </div>
  );
};

export default LocationInformation;
